import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import type { EditorRemixLifecycleEndReason } from '@atlassian/malleable-ui-components/types';

import type {
	RemixParityDurationBucket,
	RemixParityOutcome,
	RemixParityReason,
} from './remixParityObservability.ts';
import type {
	ObjectSidebarRemixDivergence,
	ObjectSidebarRemixEntryPath,
} from './objectSidebarRemixParity.ts';

export type ObjectSidebarRemixParityDimensions = Readonly<{
	entryPath: ObjectSidebarRemixEntryPath;
	pageMode: 'view' | 'edit' | 'live' | 'unknown';
}>;

export const createObjectSidebarDivergenceSignature = ({
	legacyPhase,
	machinePhase,
	comparisonPoint,
	divergenceReason,
}: ObjectSidebarRemixDivergence): string =>
	`${legacyPhase}|${machinePhase}|${comparisonPoint}|${divergenceReason}`;

export const dispatchObjectSidebarRemixParityEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	divergence: ObjectSidebarRemixDivergence,
	dimensions: ObjectSidebarRemixParityDimensions,
	/** Set when the release settled a side with this end reason, so the pair is not both observed. */
	settledByEndReason?: EditorRemixLifecycleEndReason,
): void => {
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'diverged',
				actionSubject: 'objectSidebarRemixStateParity',
				source: 'objectSidebarRemix',
				tags: ['remix', 'stateRearchitecture'],
				attributes: {
					entryPoint: 'objectSidebar',
					severity: 'critical',
					outcome:
						divergence.divergenceReason === 'terminal-outcome-mismatch'
							? 'terminal-mismatch'
							: 'released-unresolved',
					reason: divergence.divergenceReason,
					legacyPhase: divergence.legacyPhase,
					machinePhase: divergence.machinePhase,
					comparisonPoint: divergence.comparisonPoint,
					divergenceReason: divergence.divergenceReason,
					treatmentPhase: 'machine-admission-legacy-ui',
					...(settledByEndReason && { settledByEndReason }),
					...dimensions,
				},
			},
		}).fire();
	} catch {
		// Analytics are best-effort and must never affect Remix.
	}
};

export type ObjectSidebarRemixParityMechanic = Readonly<{
	legacyPhase: ObjectSidebarRemixDivergence['legacyPhase'];
	machinePhase: ObjectSidebarRemixDivergence['machinePhase'];
	comparisonPoint: ObjectSidebarRemixDivergence['comparisonPoint'];
	action: 'observed' | 'resolved';
	outcome: Extract<RemixParityOutcome, 'timing-skew' | 'converged'>;
	reason: Extract<RemixParityReason, 'awaiting-convergence' | 'phase-converged'>;
	durationBucket: RemixParityDurationBucket;
}>;

export const dispatchObjectSidebarRemixParityMechanicEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	mechanic: ObjectSidebarRemixParityMechanic,
	dimensions: ObjectSidebarRemixParityDimensions,
): void => {
	try {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: mechanic.action,
				actionSubject: 'objectSidebarRemixParityMechanic',
				source: 'objectSidebarRemix',
				tags: ['remix', 'stateRearchitecture'],
				attributes: {
					entryPoint: 'objectSidebar',
					severity: 'info',
					outcome: mechanic.outcome,
					reason: mechanic.reason,
					legacyPhase: mechanic.legacyPhase,
					machinePhase: mechanic.machinePhase,
					comparisonPoint: mechanic.comparisonPoint,
					durationBucket: mechanic.durationBucket,
					treatmentPhase: 'machine-admission-legacy-ui',
					...dimensions,
				},
			},
		}).fire();
	} catch {
		// Analytics are best-effort and must never affect Remix.
	}
};
